/**
 * `UiSpecV1`: the declarative UI an agent (or a microapp) may put on screen (D354 phase 2, docs/plans/2026-10-home-and-microapps.md,
 * "A. Generative UI blocks in conversations"). A tree of a closed vocabulary of components, validated on the host before the
 * renderer sees it and drawn by one renderer (`webview-ui/src/components/ui/uiRenderer/`) in the workbench's tokens.
 *
 * There is no code anywhere in it: no script, no HTML, no URL to load, no style, no expression. A string may carry
 * `{{binding.path|pipe}}` placeholders, which a microapp's renderer fills from its bindings' answers (the pipes are a fixed list:
 * `shared/ui/uiSpecModel.ts`); in a conversation they are shown as written. Every number that bounds a spec is a constant here,
 * and `uiSpec.test.ts` fails when one is raised without the tests being told.
 *
 * Authoring syntax: this is the JSON form (the stored and validated one). The plan names OpenUI Lang (`@openuidev/lang-core`) as a
 * streaming authoring syntax behind one adapter; the packages are not installed in this checkout, so v1 is JSON-only and the
 * adapter is the seam `parseUiSpecText` (JSON, a fenced block, or a bare object) is.
 *
 * OURS: the workbench defines this file; AIOE mirrors it with the header line, as it does `companion.ts`.
 */
import { z } from "zod";
import { UI_FIELD_NAME, UI_LIMITS, UI_TONES, uiActionSchema, viewId } from "./uiBase.js";
import { CONTROL_TYPE_LIST, childNodes, fieldKindOf, ownActions } from "../ui/uiControlRules.js";
import { controlProblems, controlSchemas } from "./uiControls/index.js";
export const UI_SPEC_VERSION = 1;
export { UI_FIELD_NAME, UI_LIMITS, UI_TONES, uiActionSchema, viewId } from "./uiBase.js";
const label = z.string().max(UI_LIMITS.label);
const hint = z.string().max(UI_LIMITS.hint);
const tone = z.enum(UI_TONES);
const name = z.string().regex(UI_FIELD_NAME, "a field name is lower-case letters, digits and underscores, starting with a letter");
const optionSchema = z
    .object({ value: z.string().min(1).max(60), label, description: hint.optional(), icon: z.string().max(40).optional(), tone: tone.optional() })
    .strict();
/** Where one stage of a `steps` tracker is. A bound list's unknown word reads as `todo`. */
export const UI_STEP_STATES = ["done", "running", "failed", "waiting", "skipped", "todo"];
/** The vocabulary, for `ui_schema` and for the unknown-component message. */
export const BASE_NODE_TYPES = [
    "stack",
    "text",
    "heading",
    "divider",
    "card",
    "choice-cards",
    "field-text",
    "field-select",
    "field-number",
    "field-toggle",
    "table",
    "stat",
    "progress",
    "steps",
    "chip",
    "button",
    "actions",
    "link",
];
/** Every node type: the first eighteen, then the controls in the order of the gallery. */
export const UI_NODE_TYPES = [...BASE_NODE_TYPES, ...CONTROL_TYPE_LIST];
/** The property every node takes. */
const tip = { tooltip: label.optional() };
const buttonSchema = z
    .object({
    type: z.literal("button"),
    ...tip,
    label: label.min(1),
    action: uiActionSchema,
    variant: z.enum(["primary", "secondary"]).optional(),
    tone: tone.optional(),
})
    .strict();
const cell = z.union([z.string().max(UI_LIMITS.label * 2), z.number(), z.boolean(), z.null()]);
/** A binding path as a spec names it: `tasks.items`, `prs`. */
const bindingPath = z.string().regex(/^[a-z][a-z0-9_]{0,39}(\.[A-Za-z0-9_]{1,40}){0,6}$/, "a binding path like tasks.items");
const children = () => z.array(z.lazy(() => uiNodeSchema)).max(UI_LIMITS.children);
export const uiNodeSchema = z.lazy(() => z.discriminatedUnion("type", [
    z.object({ type: z.literal("stack"), ...tip, direction: z.enum(["column", "row"]).optional(), gap: z.enum(["s", "m", "l"]).optional(), children: children() }).strict(),
    z.object({ type: z.literal("text"), ...tip, text: z.string().max(UI_LIMITS.text), emphasis: z.enum(["normal", "muted", "strong", "warning"]).optional() }).strict(),
    z.object({ type: z.literal("heading"), ...tip, text: label.min(1), level: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional() }).strict(),
    z.object({ type: z.literal("divider"), ...tip }).strict(),
    z.object({ type: z.literal("card"), ...tip, title: label.optional(), tone: tone.optional(), children: children() }).strict(),
    z
        .object({ type: z.literal("choice-cards"), ...tip, name, label: label.optional(), options: z.array(optionSchema).min(1).max(UI_LIMITS.choiceCards), default: z.string().max(60).optional() })
        .strict(),
    z
        .object({ type: z.literal("field-text"), ...tip, name, label: label.min(1), placeholder: label.optional(), default: z.string().max(UI_LIMITS.text).optional(), multiline: z.boolean().optional(), required: z.boolean().optional() })
        .strict(),
    z
        .object({ type: z.literal("field-select"), ...tip, name, label: label.min(1), options: z.array(optionSchema).min(1).max(UI_LIMITS.options), default: z.string().max(60).optional(), required: z.boolean().optional() })
        .strict(),
    z
        .object({
        type: z.literal("field-number"), ...tip,
        name,
        label: label.min(1),
        min: z.number().optional(),
        max: z.number().optional(),
        step: z.number().positive().optional(),
        default: z.number().optional(),
        required: z.boolean().optional(),
    })
        .strict(),
    z.object({ type: z.literal("field-toggle"), ...tip, name, label: label.min(1), default: z.boolean().optional() }).strict(),
    z
        .object({
        type: z.literal("table"), ...tip,
        caption: label.optional(),
        columns: z.array(z.object({ key: z.string().regex(/^[A-Za-z][A-Za-z0-9_.]{0,39}$/), label }).strict()).min(1).max(UI_LIMITS.tableColumns),
        rows: z.array(z.record(z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,31}$/), cell)).max(UI_LIMITS.tableRows).optional(),
        rowsFrom: bindingPath.optional(),
        rowActions: z.array(z.object({ label: label.min(1), action: uiActionSchema }).strict()).max(UI_LIMITS.rowActions).optional(),
    })
        .strict(),
    z
        .object({ type: z.literal("stat"), ...tip, label: label.min(1), value: z.union([z.string().max(UI_LIMITS.label), z.number()]), hint: hint.optional(), tone: tone.optional(), trend: z.enum(["up", "down", "flat"]).optional() })
        .strict(),
    z
        .object({ type: z.literal("progress"), ...tip, label: label.optional(), value: z.union([z.number().min(0), z.string().max(UI_LIMITS.label)]), max: z.number().positive().optional(), tone: tone.optional() })
        .strict(),
    z
        .object({
        type: z.literal("steps"), ...tip,
        label: label.optional(),
        tone: tone.optional(),
        compact: z.boolean().optional(),
        steps: z.array(z.object({ label: label.min(1), state: z.enum(UI_STEP_STATES), hint: hint.optional() }).strict()).max(UI_LIMITS.steps).optional(),
        stepsFrom: bindingPath.optional(),
    })
        .strict(),
    z.object({ type: z.literal("chip"), ...tip, text: label.min(1), tone: tone.optional() }).strict(),
    buttonSchema,
    z.object({ type: z.literal("actions"), ...tip, buttons: z.array(buttonSchema).min(1).max(UI_LIMITS.buttons) }).strict(),
    z.object({ type: z.literal("link"), ...tip, label: label.min(1), view: viewId, params: z.record(z.string().max(40), z.string().max(120)).optional() }).strict(),
    ...controlSchemas(() => uiNodeSchema),
]));
export const uiSpecSchema = z
    .object({
    v: z.literal(UI_SPEC_VERSION),
    title: label.optional(),
    alt: z.string().min(1).max(UI_LIMITS.hint * 2),
    root: uiNodeSchema,
})
    .strict();
// ── Validation: shape, then the bounds a shape cannot say ───────────────
/** Tags and control characters out of a string. React draws text as text, so this is the second line, not the first. */
export function plainText(s) {
    // eslint-disable-next-line no-control-regex
    return s.replace(/<\/?[a-zA-Z!][^>]*>/g, "").replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, "");
}
/** Every string in the tree, cleaned. Returns a new tree; the input is untouched. */
export function cleanSpecText(value) {
    if (typeof value === "string")
        return plainText(value);
    if (Array.isArray(value))
        return value.map(cleanSpecText);
    if (value && typeof value === "object") {
        const out = {};
        for (const [k, v] of Object.entries(value))
            out[k] = cleanSpecText(v);
        return out;
    }
    return value;
}
/** The nodes below a node, in document order: children, and the children of each panel. */
const childrenOf = childNodes;
/** Node count, depth and the names of the fields. */
export function measureSpec(root) {
    let nodes = 0;
    let depth = 0;
    const names = [];
    const walk = (n, d) => {
        nodes++;
        depth = Math.max(depth, d);
        if (n.type === "choice-cards" || n.type === "field-text" || n.type === "field-select" || n.type === "field-number" || n.type === "field-toggle")
            names.push(n.name);
        else if (fieldKindOf(n) && typeof n.name === "string")
            names.push(n.name);
        for (const c of childrenOf(n))
            walk(c, d + 1);
    };
    walk(root, 1);
    return { nodes, depth, fieldNames: names };
}
const issueText = (issue) => {
    const at = issue.path.length ? `${issue.path.join(".")}: ` : "";
    if (issue.code === "unrecognized_keys")
        return `${at}not allowed here: ${issue.keys.join(", ")}`;
    return `${at}${issue.message}`;
};
/**
 * Validate a spec: the shape (strict: an unknown component or property is refused with its name, never drawn loosely), then the
 * bounds. Errors are sentences an agent can act on, at most eight.
 */
export function validateUiSpec(raw, opts = {}) {
    let bytes = 0;
    try {
        bytes = JSON.stringify(raw)?.length ?? 0;
    }
    catch {
        return { ok: false, errors: ["the spec is not JSON"] };
    }
    if (bytes > UI_LIMITS.bytes)
        return { ok: false, errors: [`the spec is ${Math.round(bytes / 1024)} kB; the limit is ${UI_LIMITS.bytes / 1024} kB`] };
    const parsed = uiSpecSchema.safeParse(raw);
    if (!parsed.success) {
        const errors = parsed.error.issues.slice(0, 8).map(issueText);
        // A discriminated-union miss says "Invalid input"; say which component names exist.
        const unknown = findUnknownType(raw);
        if (unknown)
            errors.unshift(`unknown component "${unknown}"; use one of: ${BASE_NODE_TYPES.join(", ")}, or a control ui_schema lists (${CONTROL_TYPE_LIST.length} of them: charts, a data grid, form fields, navigation, a map…)`);
        return { ok: false, errors: errors.slice(0, 8) };
    }
    const spec = cleanSpecText(parsed.data);
    const m = measureSpec(spec.root);
    const errors = [];
    if (m.depth > UI_LIMITS.depth)
        errors.push(`the spec nests ${m.depth} levels; the limit is ${UI_LIMITS.depth}`);
    if (m.nodes > UI_LIMITS.nodes)
        errors.push(`the spec has ${m.nodes} components; the limit is ${UI_LIMITS.nodes}`);
    const dup = m.fieldNames.find((n, i) => m.fieldNames.indexOf(n) !== i);
    if (dup)
        errors.push(`two fields are named "${dup}"; field names must be unique`);
    if (!opts.allowRun && hasRunAction(spec.root))
        errors.push('a "run" action works only inside a microapp; in a conversation use "send"');
    errors.push(...tableProblems(spec.root));
    if (errors.length)
        return { ok: false, errors: errors.slice(0, 8) };
    return { ok: true, spec, notes: [] };
}
function findUnknownType(raw) {
    const walk = (n, d) => {
        if (!n || typeof n !== "object" || d > UI_LIMITS.depth + 2)
            return undefined;
        const t = n.type;
        if (typeof t === "string" && !UI_NODE_TYPES.includes(t))
            return t;
        const kids = [...(Array.isArray(n.children) ? (n.children) : [])];
        const panels = n.panels;
        if (Array.isArray(panels))
            for (const p of panels)
                if (p && typeof p === "object" && Array.isArray(p.children))
                    kids.push(...p.children);
        for (const k of kids) {
            const r = walk(k, d + 1);
            if (r)
                return r;
        }
        return undefined;
    };
    return walk(raw?.root, 1);
}
function hasRunAction(n) {
    if (n.type === "button")
        return n.action.do === "run";
    if (n.type === "actions")
        return n.buttons.some((b) => b.action.do === "run");
    if (n.type === "table")
        return (n.rowActions ?? []).some((r) => r.action.do === "run");
    return ownActions(n).some((a) => a.do === "run") || childrenOf(n).some(hasRunAction);
}
function tableProblems(n) {
    const out = [];
    if (n.type === "table") {
        if (!n.rows && !n.rowsFrom)
            out.push("a table needs rows or rowsFrom");
        if (n.rows && n.rowsFrom)
            out.push("a table takes rows or rowsFrom, not both");
    }
    if (n.type === "steps") {
        if (!n.steps && !n.stepsFrom)
            out.push("steps needs steps or stepsFrom");
        if (n.steps && n.stepsFrom)
            out.push("steps takes steps or stepsFrom, not both");
    }
    out.push(...controlProblems(n));
    for (const c of childrenOf(n))
        out.push(...tableProblems(c));
    return out;
}
/** The text of a spec's first heading, stat or text node: what a card or a log line says about it. */
export function specHeadline(spec) {
    if (spec.title)
        return spec.title;
    let found = "";
    const walk = (n) => {
        if (found)
            return;
        if (n.type === "heading")
            found = n.text;
        else if (n.type === "card" && n.title)
            found = n.title;
        else
            for (const c of childrenOf(n))
                walk(c);
    };
    walk(spec.root);
    return found || spec.alt;
}
/**
 * Take a spec out of what a model wrote: a bare JSON object, or text with a fenced ```ui block (or ```json). The one place a
 * streaming syntax (OpenUI Lang) would plug in; v1 reads JSON only.
 */
export function parseUiSpecText(text) {
    const t = text.trim();
    const fenced = /```(?:ui|json)?[^\S\n]*\n?([\s\S]*?)```/.exec(t);
    const body = (fenced ? fenced[1] : t).trim();
    if (!body)
        return { ok: false, error: "empty" };
    try {
        return { ok: true, value: JSON.parse(body) };
    }
    catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
    }
}
/**
 * A flat JSON Schema for the spec, for a local model that cannot follow a long prompt (`ui_render`'s input schema): one node
 * shape with an enumerated `type`, every other property optional. The strict zod above is still what accepts it.
 */
export function uiSpecJsonSchema() {
    const node = {
        type: "object",
        description: `A component. type is one of ${UI_NODE_TYPES.join(", ")}. stack, card and the other containers hold children; tabs and accordion hold panels; actions holds buttons; table, data-grid and the charts hold columns, rows and series; ui_schema lists every property of every type.`,
        properties: {
            type: { type: "string", enum: [...UI_NODE_TYPES] },
            text: { type: "string" },
            label: { type: "string" },
            title: { type: "string" },
            name: { type: "string", description: "A field's name: lower-case letters, digits, underscores." },
            value: { description: "A stat's or progress's value." },
            hint: { type: "string" },
            tone: { type: "string", enum: [...UI_TONES] },
            children: { type: "array", items: { type: "object" } },
            panels: { type: "array", description: "tabs, accordion, carousel: [{ id, label, children[] }].", items: { type: "object" } },
            series: { type: "array", description: "A chart's series: [{ key, label?, tone? }].", items: { type: "object" } },
            rowsFrom: { type: "string", description: "A binding's list (apps): its path, like tasks.items." },
            buttons: { type: "array", items: { type: "object" } },
            options: { type: "array", items: { type: "object", properties: { value: { type: "string" }, label: { type: "string" }, description: { type: "string" } }, required: ["value", "label"] } },
            columns: { type: "array", items: { type: "object", properties: { key: { type: "string" }, label: { type: "string" } }, required: ["key", "label"] } },
            rows: { type: "array", items: { type: "object" } },
            steps: { type: "array", description: `steps: the stages, each { label, state: ${UI_STEP_STATES.join("|")}, hint? }.`, items: { type: "object" } },
            action: { type: "object", description: 'What a press does: {"do":"send","text":"…"} or {"do":"open-view","view":"tasks"} or {"do":"reset"}.' },
        },
        required: ["type"],
    };
    return {
        type: "object",
        properties: {
            v: { type: "integer", enum: [1] },
            title: { type: "string" },
            alt: { type: "string", description: "One plain sentence saying what the block asks or shows, for places that cannot draw it." },
            root: node,
        },
        required: ["v", "alt", "root"],
    };
}
//# sourceMappingURL=uiSpec.js.map